import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import axios from 'axios';
import { setImpersonateId } from '../impersonation';

const API = process.env.REACT_APP_API_URL;
const RECONNECT_FOR_MS = 30000;
const UserContext = createContext(null);

export function UserProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userLoading, setUserLoading] = useState(true);
  const [impersonatingId, setImpersonatingId] = useState(null); // user ID being impersonated
  // 'reconnecting' while the server doesn't answer (a deploy restart is ~10s),
  // 'unreachable' once retries run out. Only a real answer (401/403/404) means
  // there is no account — before 2026-10-23 every failure read as
  // "Access denied — contact an admin", including a deploy restart.
  const [connection, setConnection] = useState('ok');

  const loadUser = useCallback(async (impId = null) => {
    const started = Date.now();
    for (let attempt = 0; ; attempt++) {
      try {
        const session = await fetchAuthSession();
        const token = session.tokens?.idToken?.toString();
        if (token) {
          const headers = { Authorization: `Bearer ${token}` };
          if (impId) headers['X-Impersonate'] = impId;
          const res = await axios.get(`${API}/users/me`, { headers });
          setCurrentUser(res.data);
        }
        setConnection('ok');
        break;
      } catch (e) {
        const status = e?.response?.status;
        if (status && status < 500) {  // the server answered: no account
          setCurrentUser(null);
          setConnection('ok');
          break;
        }
        if (Date.now() - started > RECONNECT_FOR_MS) {
          setCurrentUser(null);
          setConnection('unreachable');
          break;
        }
        setConnection('reconnecting');
        await new Promise(r => setTimeout(r, Math.min(1000 * 2 ** attempt, 5000)));
      }
    }
    setUserLoading(false);
  }, []);

  useEffect(() => { loadUser(impersonatingId); }, [impersonatingId, loadUser]);

  const startImpersonating = (userId) => {
    setImpersonateId(userId);   // keep interceptor in sync
    setImpersonatingId(userId);
  };
  const stopImpersonating = () => {
    setImpersonateId(null);
    setImpersonatingId(null);
  };

  return (
    <UserContext.Provider value={{
      currentUser,
      userLoading,
      connection,
      reloadUser: () => loadUser(impersonatingId),
      impersonatingId,
      startImpersonating,
      stopImpersonating,
    }}>
      {children}
    </UserContext.Provider>
  );
}

export function useCurrentUser() {
  return useContext(UserContext);
}

// 2026-09-28: 'contentiq' + 'contentiq-assets' + 'contentiq-builds' added —
// ContentIQ's PM/editor side opened to program managers with the deployment
// extension (backend migration 0116 opened the routes + nav rows; the
// core-config page 'contentiq-admin' stays admin-only, matching the four
// admin-only catalog writes the route guard pins).
const PM_PAGES = ['home', 'programs', 'program-documents', 'components', 'goals', 'lead-upload', 'lead-qa', 'qa-rules', 'lead-delivery', 'client-delivery-wizard', 'invoices', 'targeting-lists', 'suppliers', 'reports-billable-supplier', 'reports-billable-client', 'pm-home', 'week-glance', 'contentiq', 'contentiq-assets', 'contentiq-builds'];

export function canAccess(role, page) {
  if (!role) return false;
  const access = {
    admin:           ['home', 'program-new', 'clients', 'products', 'programs', 'program-documents', 'components', 'goals', 'lead-upload', 'lead-qa', 'qa-rules', 'lead-delivery', 'lead-delivery-setup', 'client-delivery-wizard', 'invoices', 'targeting-lists', 'suppliers', 'reports-billable-supplier', 'reports-billable-client', 'reports-revenue', 'admin', 'email-alerts', 'component-library', 'navigation', 'recycle-bin', 'backlog-queue', 'system-health', 'global-opt-outs', 'pm-home', 'week-glance', 'supplier-portal', 'contentiq', 'contentiq-admin', 'contentiq-assets', 'contentiq-builds'],
    financial:       ['home', 'clients', 'products', 'programs', 'components', 'goals', 'lead-upload', 'invoices', 'targeting-lists', 'suppliers', 'reports-billable-supplier', 'reports-billable-client', 'reports-revenue'],
    program_manager: PM_PAGES,
    // A supporting PM has the same permissions as a PM (backend auth.py
    // ROLE_ALIASES); the roles differ only in how they attach to a program.
    supporting_program_manager: PM_PAGES,
    // 2026-08-28: 'week-glance' added — a supplier now gets a filtered view
    // of the board (their own owned/co-owned items only, backend-scoped in
    // GET /week-board/programs) rather than no access at all.
    // 'pm-home' dropped 2026-08-31 — the supplier experience unified into
    // supplier-portal (Your Tasks + Assigned Programs & Lead Status + Upload
    // + Delivery History, one page) instead of a separate My Home. The nav
    // DB row still needs a manual /admin/navigation cleanup to match.
    // 2026-09-28: 'contentiq-builds' — the deployment supplier's Landing
    // Page Builds queue (ContentIQ deployment extension). The only ContentIQ
    // page a partner can reach, mirroring the backend's partner allowlist.
    partner:         ['home', 'supplier-portal', 'week-glance', 'contentiq-builds'],
  };
  return (access[role] || []).includes(page);
}
